import { useTranslation } from 'react-i18next';
import { formatHour, hasAsweredFunctionsByType, validationFunctionsByType } from '../helper/answer';
import { AnswerValueType } from '../types/Answer';
import { CheckboxQuestionType, HourQuestionType, NumberQuestionType, QuestionType, QuestionTypeEnum } from '../types/Question';

interface AnswerRuleHook {
  isAnswerValid: boolean;
  translatedRuleMsg: string;
}

const useAnswerRules = (question: QuestionType, answer: AnswerValueType): AnswerRuleHook => {
  const { t } = useTranslation();
  const { is_mandatory_question: isMandatory, type } = question;

  // INFORMATION pages are always valid regardless of any flags: hasAswered stays false
  // (no answer row must ever look "answered" to the completion heuristics), and going
  // through the standard path below would disable "Next" if mandatory were ever set.
  if (type === QuestionTypeEnum.INFORMATION) {
    return { translatedRuleMsg: '', isAnswerValid: true };
  }

  const userHasAnswered = hasAsweredFunctionsByType[type](answer);

  const isAnswerValid: boolean = userHasAnswered ?
    validationFunctionsByType[type](question, answer) :
    !isMandatory;

  const translatedRuleByTypes: Record<QuestionTypeEnum, (question: QuestionType) => string> = {
    CHECKBOX: (question: QuestionType): string => {
      const { detail: { rules: { constraint, quantity } } } = question as CheckboxQuestionType;
      return t(`answer_checkbox_constraint_${constraint}`, { quantity });
    },
    RADIOBUTTON: () => t('answer_radio_constraint'),
    HOUR: (question: HourQuestionType): string => {
      const { detail: { format, min_hour, max_hour } } = question;

      const min = formatHour(min_hour, format);
      const max = formatHour(max_hour, format);

      return t(`answer_hour_constraint`, { min, max });

    },
    NUMBER: (question: QuestionType): string => {
      // Identity phone question: the min/max/decimals wording would be nonsense.
      if (question.identity_target === 'phone_number') return t('answer_phone_constraint');
      const { detail: { min_value: min, max_value: max, number_decimal: nbDecimales } } = question as NumberQuestionType;
      const minMaxString = t(`answer_number_constraint_min_max`, { min, max });
      const decString = t(`answer_number_constraint_decimal`, { nbDecimales });

      return `${minMaxString} ${decString}`;
    },
    DATE: () => t('answer_date_constraint'),
    SLIDER: () => '',
    DROPDOWN: () => t('answer_dropdown_constraint'),
    TEXT: () => t('answer_text_constraint'),
    INFORMATION: () => ''
  }

  return {
    translatedRuleMsg: translatedRuleByTypes[question.type](question),
    isAnswerValid
  };
}

export default useAnswerRules;
